<template>
  <v-container fluid>
    <v-row align="start" class="vehicle-inspection" justify="start">
      <v-col>
        <v-data-table
          :class="{'not-default-page-size': !this.isDefaultSize}"
          :footer-props="footerProps"
          :headers="headers"
          :items="displayVehicleInspections"
          :loading="pageLoading || !tableInited"
          :options="{
            page: pageIndex,
            itemsPerPage: pageSize,
            sortBy: sort.sortBy,
            sortDesc: sort.sortDesc,
          }"
          :expanded.sync="expanded"
          show-expand
          class="elevation-1"
          :server-items-length="totalCount"
          multi-sort
          @update:options="paginate($event)"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title class="d-flex align-center">
                {{ $localizationService.localize("vehicle_inspection_page.title") }}
                <TableSettings
                  :pDefaultButtonTooltip="
                    $localizationService.localize(
                      'vehicle_inspection_page.settings_tooltip.apply_default'
                      )
                  "
                  :pIsVisible="tableInited && !isDefaultSettings"
                  :pSaveButtonTooltip="
                    $localizationService.localize(
                      'vehicle_inspection_page.settings_tooltip.save'
                    )
                  "
                  class="ml-5"
                  @onApplyDefaultSettings="applyDefaultSettings()"
                  @onSaveSettings="saveSettings()"
                />
              </v-toolbar-title>
              <v-spacer/>
              <div class="d-flex mt-6">
                <v-select
                  :items="INSPECTIONS_FAIL_PASS_VALUES"
                  item-text="text"
                  item-value="value"
                  label="Fail/Pass Filter"
                  class="mr-4 filter-field"
                  :value=filter
                  @change="changeFilter($event)"
                />
                <v-text-field
                  v-model="searchString"
                  :label="$localizationService.localize('vehicle_inspection_page.search')"
                  class="search-field"
                  hide-details
                  width="200"
                  @input="loadOnInputSearchString()"
                />
              </div>
              <ConfirmationDialog ref="deleteDialog"/>
            </v-toolbar>
          </template>
          <template v-for="header in headers" v-slot:[`header.${header.value}`]>
            <span
              :key="header.value"
              :class="{
              'not-default-header-sort': isNotDefaultSortItems.includes(header.value)
              }"
            >
              {{ header.text }}
            </span>
          </template>
          <template v-slot:[`item.assignment.targetVehicle`]="{ item }">
            {{ item.assignment.targetVehicle.name }}
          </template>
          <template v-slot:[`item.assignment.targetScheme`]="{ item }">
            {{ item.assignment.targetScheme.name }}
          </template>
          <template v-slot:[`item.inspector`]="{ item }">
            {{ item.inspector.name }}
          </template>
          <template v-slot:[`item.assignment.targetDate`]="{ item }">
            {{ momentDate(item.assignment.targetDate) }}
          </template>
          <template v-slot:[`item.startDate`]="{ item }">
            {{ momentDateAndTime(item.startDate) }}
          </template>
          <template v-slot:[`item.endDate`]="{ item }">
            {{ momentDateAndTime(item.endDate) }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-icon color="red" medium @click="openDeleteVehicleInspectionDialog(item)">
              mdi-delete
            </v-icon>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <VehicleInspectionData :inspection=item />
            </td>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>

import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import lodash from "lodash";
import tableSortParserMixin from "../../utils/table-sort-parser";
import ConfirmationDialog from "../ConfirmationDialog";
import TableSettings from "../TableSettings";
import { momentDateAndTime, momentDate } from "@/utils/utils";
import VehicleInspectionData from "./VehicleInspectionData";
import { INSPECTIONS_FAIL_PASS_VALUES } from "../../utils/utils";

const locService = Vue.prototype.$localizationService;

export default {
  name: "VehicleInspections",

  mixins: [tableSortParserMixin],

  components: {
    VehicleInspectionData,
    ConfirmationDialog,
    TableSettings,
  },

  computed: {
    ...mapState("vehicleInspectionModule", [
      "displayVehicleInspections",
      "totalCount",
      "currentPage",
      "pageLoading",
    ]),
    ...mapState("vehicleInspectionTableSettingsModule", [
      "pageIndex",
      "pageSize",
      "sort",
      "filter",
      "defaultSettings",
    ]),
    ...mapGetters("vehicleInspectionTableSettingsModule", [
      "isDefaultSettings",
      "isDefaultSort",
      "isDefaultSize",
      "isDefaultFilter",
    ]),

    isNotDefaultSortItems() {
      return !this.isDefaultSort
        ? this.getNotDefaultTableSortItems("sort")
        : [];
    },
  },
  data: () => ({
    INSPECTIONS_FAIL_PASS_VALUES,

    footerProps: {
      "items-per-page-options": [25, 50, 100],
    },

    tableInited: false,

    searchString: "",

    expanded: [],

    headers: [
      {
        text: locService.localize("vehicle_inspection_page.header.target_vehicle"),
        value: "assignment.targetVehicle",
        sortable: true,
      },
      {
        text: locService.localize("vehicle_inspection_page.header.target_scheme"),
        value: "assignment.targetScheme",
        sortable: true,
      },
      {
        text: locService.localize("vehicle_inspection_page.header.target_date"),
        value: "assignment.targetDate",
        sortable: true,
      },
      {
        text: locService.localize("vehicle_inspection_page.header.start_date"),
        value: "startDate",
        sortable: true,
      },
      {
        text: locService.localize("vehicle_inspection_page.header.end_date"),
        value: "endDate",
        sortable: true,
      },
      {
        text: locService.localize("vehicle_inspection_page.header.inspector"),
        value: "inspector",
        sortable: false,
      },
      {
        text: locService.localize("vehicle_inspection_page.header.actions"),
        value: "actions",
        align: "end",
        sortable: false,
      },
    ],
  }),

  methods: {
    momentDateAndTime,
    momentDate,

    paginate(data) {
      this.$store.commit("vehicleInspectionTableSettingsModule/changeSettings", {
        index: data.page,
        size: data.itemsPerPage,
        sort: {
          sortBy: [...data.sortBy],
          sortDesc: [...data.sortDesc],
        },
        filter: this.filter,
      });

      this.loadPage({
        index: data.page - 1,
        size: data.itemsPerPage,
        sort: data.sortBy.map((it, i) => (data.sortDesc[i] ? `-${it}` : it)),
        searchString: this.searchString,
        filter: this.filter,
      });
    },

    loadPage: lodash.debounce(async function (currentPage) {
      await this.$store.dispatch("vehicleInspectionModule/loadPage", currentPage);
      this.tableInited = true;
    }, 500),

    changeFilter(data) {
      this.$store.commit("vehicleInspectionTableSettingsModule/changeFilterSettings", {
        filter: data,
      });
      this.updateRoutePath();

      const newPage = { ...this.currentPage, filter: this.filter };

      this.$store.dispatch("vehicleInspectionModule/loadPage", newPage);
    },

    saveSettings() {
      this.$store.commit("vehicleInspectionTableSettingsModule/saveSettings");

      Vue.toasted.success(
        locService.localize("vehicle_inspection_page.settings_msg.save"),
        { duration: 3000 },
      );
    },

    applyDefaultSettings() {
      this.$store.commit("vehicleInspectionTableSettingsModule/applyDefaultSettings");
    },

    reloadPage() {
      this.$store.dispatch("vehicleInspectionModule/reloadPage");
    },

    openDeleteVehicleInspectionDialog(vehicleInspection) {
      this.$refs.deleteDialog.openDialog({
        header: locService.localize("vehicle_inspection_page.dialog.delete.title"),
        text: locService.localize("vehicle_inspection_page.dialog.delete.text"),
        submitText: locService.localize("btn.delete"),
        submitColor: "red",
        onSubmit: async () => {
          await this.$store.dispatch("vehicleInspectionModule/deleteVehicleInspection", vehicleInspection);
          this.reloadPage();
        },
      });
    },

    updateRoutePath() {
      const params = new URLSearchParams();

      params.append("pageSize", this.pageSize);

      params.append("pageIndex", this.pageIndex);

      params.append("sort", JSON.stringify(this.sort));

      if (this.searchString) {
        params.append("searchString", this.searchString);
      }

      if (this.filter) {
        params.append("filter", this.filter);
      }

      if (
        this.$route.query.pageSize != (params.get("pageSize") || undefined)
          || this.$route.query.pageIndex != (params.get("pageIndex") || undefined)
          || this.$route.query.searchString != (params.get("searchString") || undefined)
          || this.$route.query.filter != (params.get("filter") || undefined)
          || this.$route.query.sort != (params.get("sort") || undefined)
      ) {
        this.$router.push(`/vehicle-inspection?${params.toString()}`);
      }
    },

    loadOnInputSearchString: lodash.debounce(function () {
      const newPage = { ...this.currentPage };

      newPage.searchString = this.searchString;

      this.$store.dispatch("vehicleInspectionModule/loadPage", newPage);
      this.updateRoutePath();
    }, 600),
  },

  created() {
    this.$store.dispatch("vehicleInspectionTableSettingsModule/loadDefaultSettingsFromStorage");

    const query = this.$route.query || {};

    if (!Object.keys(query).length) {
      this.$store.commit("vehicleInspectionTableSettingsModule/applyDefaultSettings");
    } else {
      this.$store.commit("vehicleInspectionTableSettingsModule/applyQuerySettings", query);

      if (query.searchString) {
        this.searchString = query.searchString;
      }
      if (query.filter) {
        this.filter = query.filter;
      }
    }
  },

};
</script>

<style lang="scss">
.vehicle-inspection {
  .search-field {
    max-width: 250px;
  }

  .filter-field {
    max-width: 150px;
  }

  .v-data-table-header-mobile {
    display: none;
  }

  tr.v-data-table__expanded__content {
    box-shadow: none !important;
    background-color: rgba(50, 50, 50, 0.03);
  }

}
</style>
